import { useMemo } from 'react';
import classNames from 'classnames/bind';
import { optionalProp } from '../../../core';
import { FAIcon } from '../../../Icons/Icons';
import type { PaginationProps } from '../../types/propType';
import { Position } from '../../../types/enums';
import { scrollToTop } from '../Pagination';
import { useTranslation } from '../../../Translation';
import { usePaginationPosition } from '../../../hooks';
import { getPaginationRange } from '../utils';
import PaginationRecordRange from '../PaginationRecordRange/PaginationRecordRange';
import styles from './AdminPagination.module.scss';

const MAX_SLOTS = 7;

const cx = classNames.bind(styles);
const AdminPagination = ({
  totalRecords,
  currentPage = 0,
  rowsPerPage = 10,
  updatePaginationData,
  startRecordIndex = () => 1,
  endRecordIndex,
  goToPreviousPage,
  goToNextPage,
  formatPaginationCallback,
  ariaLabelNextPage,
  ariaLabelPrevPage,
  hasNextPage,
  scrollToTopRefId,
  paginationPosition = Position.CENTER,
  enableStickyPagination,
}: PaginationProps) => {
  const numberOfPages = Math.ceil(totalRecords / rowsPerPage);
  const t = useTranslation();

  const ariaLabelNextPageValue = ariaLabelNextPage || t('nextgen.NextPage.ariaLabel');
  const ariaLabelPrevPageValue = ariaLabelPrevPage || t('nextgen.PreviousPage.ariaLabel');

  usePaginationPosition(enableStickyPagination, currentPage, totalRecords, scrollToTopRefId, rowsPerPage);

  const pageArr = useMemo(
    () => getPaginationRange(currentPage, totalRecords, rowsPerPage, MAX_SLOTS),
    [currentPage, totalRecords, rowsPerPage],
  );
  const goToPage = (page: number) => {
    const nextPage = page;
    updatePaginationData({
      currentPage: nextPage,
      recordStartIndex: startRecordIndex?.(nextPage, rowsPerPage),
      recordEndIndex: endRecordIndex?.(nextPage, rowsPerPage),
    });
    scrollToTop(scrollToTopRefId);
  };

  const disableNextButton = () => numberOfPages === currentPage + 1 || hasNextPage;
  const CPaginationContainerClassname = cx('pagination-container', {
    'pagination-container-end': paginationPosition === Position.RIGHT,
    'sticky-pagination': enableStickyPagination,
  });
  return (
    <div id={'pagination-container'} className={CPaginationContainerClassname}>
      <div className={styles['pagination-next-prev-controls']}>
        <div
          role="button"
          aria-label={ariaLabelPrevPageValue}
          tabIndex={0}
          className={`${styles['icon-container']} ${currentPage === 0 ? styles['inactive'] : ''} `}
          data-testid="pagination-back-button"
          onClick={goToPreviousPage}
          onKeyDown={goToPreviousPage}
        >
          <FAIcon iconName={'angle-left'} className={styles['icon']} />
        </div>
        <div className={styles['pagination-page-numbers']} data-testid="page-numbers">
          {pageArr.map((v, i) => (
            <div
              key={`${v}-${i + 1}`}
              role="presentation"
              className={`${styles['page-number']} ${
                typeof v === 'number' && currentPage + 1 === v ? styles['active'] : ''
              }`}
              data-testid={`page-numbers-${i}`}
              onKeyDown={() => typeof v === 'number' && goToPage(v - 1)}
              onClick={() => typeof v === 'number' && goToPage(v - 1)}
            >
              {v}
            </div>
          ))}
        </div>
        <div
          role="button"
          aria-label={ariaLabelNextPageValue}
          tabIndex={0}
          className={`${styles['icon-container']} ${disableNextButton() ? styles['inactive'] : ''}`}
          data-testid="pagination-next-button"
          onClick={() => !disableNextButton() && goToNextPage?.()}
          onKeyDown={goToNextPage}
        >
          <FAIcon iconName={'angle-right'} className={styles['icon']} />
        </div>
      </div>
      <PaginationRecordRange
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        totalRecords={totalRecords}
        startRecordIndex={startRecordIndex || (() => 0)}
        endRecordIndex={endRecordIndex || (() => 0)}
        {...optionalProp('formatPaginationCallback', formatPaginationCallback)}
      />
    </div>
  );
};

export default AdminPagination;
